import { useState, useEffect, useCallback, useSyncExternalStore } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  getDeferredPrompt,
  clearDeferredPrompt,
  wasAppInstalled,
  subscribeInstallState,
  isStandaloneDisplay,
} from "@/lib/pwaInstall";

// localStorage keys
const STORAGE_KEYS = {
  INSTALLED: "dallali-pwa-installed",
  DISMISSED_UNTIL: "dallali-pwa-dismissed-until",
  MODAL_DISMISSED: "dallali-pwa-modal-dismissed",
  SHOW_INSTALL_MODAL: "dallali-pwa-show-install-modal",
} as const;

// Dismissal duration: 7 days in milliseconds
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

// Device detection helpers
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream;
  const isAndroid = /Android/.test(ua);
  const hasShareAPI = typeof navigator.share === "function";
  
  return { isIOS, isAndroid, hasShareAPI };
};

interface UsePWAInstallReturn {
  /** Whether the banner should be visible */
  isBannerVisible: boolean;
  /** Whether the install modal should be visible (post-login) */
  isModalVisible: boolean;
  /** Whether the app is already installed */
  isInstalled: boolean;
  /** Whether the browser supports PWA installation */
  isInstallable: boolean;
  /** Whether device is iOS (iPhone/iPad) */
  isIOS: boolean;
  /** Whether device is Android */
  isAndroid: boolean;
  /** Whether navigator.share() is available */
  hasShareAPI: boolean;
  /** Trigger the native install prompt */
  triggerInstall: () => Promise<void>;
  /** Trigger iOS share sheet */
  triggerIOSShare: () => Promise<void>;
  /** Dismiss the banner temporarily (7 days) */
  dismissTemporarily: () => void;
  /** Dismiss the banner for the current session only */
  dismissForSession: () => void;
  /** Dismiss the modal permanently */
  dismissModalPermanently: () => void;
}

export function usePWAInstall(): UsePWAInstallReturn {
  const isMobile = useIsMobile();
  // Install event + appinstalled flag come from the app-wide store (registered
  // at module load in main.tsx) so a `beforeinstallprompt` that fired before
  // this hook mounted is not lost.
  const deferredPrompt = useSyncExternalStore(subscribeInstallState, getDeferredPrompt, () => null);
  const appInstalled = useSyncExternalStore(subscribeInstallState, wasAppInstalled, () => false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissedForSession, setIsDismissedForSession] = useState(false);
  const [isModalDismissed, setIsModalDismissed] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  
  // Device info
  const [deviceInfo] = useState(() => getDeviceInfo());

  // Check if already installed via display-mode or localStorage
  useEffect(() => {
    // Check if running in standalone mode (already installed)
    const isStandalone = isStandaloneDisplay();
    const wasInstalled = localStorage.getItem(STORAGE_KEYS.INSTALLED) === "true";

    if (isStandalone || wasInstalled) {
      setIsInstalled(true);
    }
    
    // Check if modal was permanently dismissed
    const modalDismissed = localStorage.getItem(STORAGE_KEYS.MODAL_DISMISSED) === "true";
    setIsModalDismissed(modalDismissed);
    
    // Check if we should show install modal (set by Auth.tsx after login)
    const shouldShowModal = localStorage.getItem(STORAGE_KEYS.SHOW_INSTALL_MODAL) === "true";
    if (shouldShowModal && !isStandalone && !wasInstalled && !modalDismissed) {
      setShowInstallModal(true);
      // Clear the flag after reading
      localStorage.removeItem(STORAGE_KEYS.SHOW_INSTALL_MODAL);
    }
  }, []);

  // Check if temporarily dismissed
  const isTemporarilyDismissed = useCallback(() => {
    const dismissedUntil = localStorage.getItem(STORAGE_KEYS.DISMISSED_UNTIL);
    if (!dismissedUntil) return false;
    
    const dismissedUntilTimestamp = parseInt(dismissedUntil, 10);
    return Date.now() < dismissedUntilTimestamp;
  }, []);

  // React to a completed install (appinstalled, surfaced via the store): mark
  // installed, persist the flag, and close any open install modal.
  useEffect(() => {
    if (!appInstalled) return;
    setIsInstalled(true);
    setShowInstallModal(false);
    localStorage.setItem(STORAGE_KEYS.INSTALLED, "true");
  }, [appInstalled]);

  // Trigger the native install prompt
  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    try {
      // Show the browser's native install prompt
      await deferredPrompt.prompt();

      // Wait for user response
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        localStorage.setItem(STORAGE_KEYS.INSTALLED, "true");
        setIsInstalled(true);
        setShowInstallModal(false);
      }
    } catch (_error) {
      // Installation prompt failed - hide modal
      setShowInstallModal(false);
    } finally {
      // Clear the prompt (can only be used once)
      clearDeferredPrompt();
    }
  }, [deferredPrompt]);

  // Trigger iOS share sheet
  const triggerIOSShare = useCallback(async () => {
    if (!deviceInfo.hasShareAPI) return;
    
    try {
      await navigator.share({
        url: window.location.origin,
        title: "دلّالي",
        text: "الوصول السريع لتطبيق دلّالي",
      });
      // User completed share sheet - mark as handled
      localStorage.setItem(STORAGE_KEYS.MODAL_DISMISSED, "true");
      setIsModalDismissed(true);
      setShowInstallModal(false);
    } catch (_error) {
      // User cancelled share sheet - just dismiss for session
      setShowInstallModal(false);
    }
  }, [deviceInfo.hasShareAPI]);

  // Dismiss banner for 7 days
  const dismissTemporarily = useCallback(() => {
    const dismissUntil = Date.now() + DISMISS_DURATION_MS;
    localStorage.setItem(STORAGE_KEYS.DISMISSED_UNTIL, dismissUntil.toString());
    setIsDismissedForSession(true);
  }, []);

  // Dismiss banner for current session only
  const dismissForSession = useCallback(() => {
    setIsDismissedForSession(true);
  }, []);

  // Dismiss modal permanently
  const dismissModalPermanently = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.MODAL_DISMISSED, "true");
    setIsModalDismissed(true);
    setShowInstallModal(false);
  }, []);

  // Calculate banner visibility (for bottom banner)
  const isBannerVisible =
    isMobile &&
    deferredPrompt !== null &&
    !isInstalled &&
    !isDismissedForSession &&
    !isTemporarilyDismissed();

  // Calculate modal visibility (for post-login modal)
  const isModalVisible =
    isMobile &&
    showInstallModal &&
    !isInstalled &&
    !isModalDismissed;

  const isInstallable = deferredPrompt !== null;

  return {
    isBannerVisible,
    isModalVisible,
    isInstalled,
    isInstallable,
    isIOS: deviceInfo.isIOS,
    isAndroid: deviceInfo.isAndroid,
    hasShareAPI: deviceInfo.hasShareAPI,
    triggerInstall,
    triggerIOSShare,
    dismissTemporarily,
    dismissForSession,
    dismissModalPermanently,
  };
}
